// src/pages/DebtStudio/components/Reports/DebtServiceReport.jsx

import React, { useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import ExcelGrid from "components/ExcelGrid";
import dayjs from "lib/dayjs";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import ChartTooltip from "components/ChartTooltip";
import { makeNiceScaleZeroBased } from "lib/chartScale";
import { buildPrincipalPeriods } from "pages/DebtStudio/lib/buildPrincipalPeriods";

import DebtServiceTable from "./DebtServiceTable";

/** ---------- Helpers ---------- */
const fmt2 = (v) =>
  v != null
    ? Number(v).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";

const formatKMB = (v) => {
  const a = Math.abs(v);
  if (a >= 1e9)
    return `${(v / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })}B`;
  if (a >= 1e6)
    return `${(v / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
  if (a >= 1e3)
    return `${(v / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return (v || 0).toLocaleString();
};

const formatFull2dp = (v, s = "$") =>
  `${s}${(Number(v) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function netDebtService(row) {
  const explicit = Number(row?.netDebtService);
  if (Number.isFinite(explicit)) return explicit;

  return num(row?.total, 0) + num(row?.debtServiceAdjustment ?? row?.adjustment, 0);
}

function existingDebtService(row) {
  const explicit = Number(
    row?.existingCashFlows ??
      row?.existingDebtService ??
      row?.baseDebtService ??
      0
  );

  return Number.isFinite(explicit) ? explicit : 0;
}

function aggregateDebtService(row) {
  const explicit = Number(
    row?.aggregateCashFlows ??
      row?.aggregateDebtService ??
      row?.aggregateNetDebtService
  );

  if (Number.isFinite(explicit)) return explicit;

  return netDebtService(row) + existingDebtService(row);
}

function maxDate(rows) {
  let m = null;
  for (const r of rows || []) {
    const d = dayjs(r.date);
    if (!m || d.isAfter(m)) m = d;
  }
  return m;
}

const comparisonMoney = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const comparisonColumns = [
  { key: "date", label: "Period Ending", width: "w-40", date: true },
  { key: "priorDebtService", label: "Prior Debt Service", numeric: true, width: "w-44", className: "text-right numeric", headerClassName: "text-right", format: comparisonMoney },
  { key: "refundingDebtService", label: "Refunding Debt Service", numeric: true, width: "w-48", className: "text-right numeric", headerClassName: "text-right", format: comparisonMoney },
  { key: "savings", label: "Savings", numeric: true, width: "w-44", className: (row) => `text-right numeric ${Number(row.savings) < -0.01 ? "text-danger" : ""}`, headerClassName: "text-right", format: comparisonMoney },
];

/**
 * Debt Service (Merged Tab)
 * ---------------------------------------------------------
 * CapI-style layout:
 * - KPIs (top)
 * - Chart (middle)
 * - Table (bottom)
 *
 * plotRef wraps the chart container so PDF export can capture it.
 */
export default function DebtServiceReport({ rows = [], form = {}, plotRef = null, refundingComparison = [] }) {
  const [view, setView] = useState("refunding");
  const schedule = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const datedDate = form?.datedDate || form?.dated_date || null;
  const firstPrincipalDate =
    form?.firstPrincipalDate || form?.first_principal_date || null;
  const principalFrequency =
    form?.principalFrequency || form?.principal_frequency || "annual";

  /** ---------- KPIs ---------- */
  const kpis = useMemo(() => {
    if (!schedule.length) return null;

    let totalPrincipal = 0;
    let totalInterest = 0;
    let grossDebtService = 0;
    let totalAdjustments = 0;
    let netDebtServiceTotal = 0;

    for (const r of schedule) {
      const adjustment = num(r.debtServiceAdjustment ?? r.adjustment, 0);

      totalPrincipal += num(r.principal, 0);
      totalInterest += num(r.interest, 0);
      grossDebtService += num(r.total, 0);
      totalAdjustments += adjustment;
      netDebtServiceTotal += netDebtService(r);
    }

    const hasAdjustments = Math.abs(totalAdjustments) > 0.005;

    return {
      totalPrincipal,
      totalInterest,
      grossDebtService,
      totalAdjustments,
      netDebtServiceTotal,
      hasAdjustments,
    };
  }, [schedule]);

  /** ---------- Chart Data ---------- */
  const couponData = useMemo(
    () =>
      schedule.map((r) => ({
        date: r.date,
        principal: num(r.principal, 0),
        interest: num(r.interest, 0),

        // New issue net debt service only
        total: netDebtService(r),

        // Existing debt service should only plot at period-total points
        existingDebtService: null,
        aggregateDebtService: null,
      })),
    [schedule]
  );

  const periodTotals = useMemo(() => {
    if (!schedule?.length || !datedDate || !firstPrincipalDate) return [];

    const last = maxDate(schedule);
    const allPeriods = buildPrincipalPeriods({
      startDate: datedDate,
      periodEndAnchor: firstPrincipalDate,
      lastCashflowDate: last,
      principalFrequency,
    });

    if (!allPeriods.length) return [];

    return allPeriods
      .map((p) => {
        const totals = schedule.reduce(
          (acc, r) => {
            const d = dayjs(r.date);

            const inPeriod =
              d.isAfter(p.start, "day") &&
              (d.isBefore(p.end, "day") || d.isSame(p.end, "day"));

            if (!inPeriod) return acc;

            acc.existing += existingDebtService(r);
            acc.aggregate += aggregateDebtService(r);

            return acc;
          },
          {
            existing: 0,
            aggregate: 0,
          }
        );

        return {
          date: p.end.format("YYYY-MM-DD"),

          existingDebtService:
            Math.abs(totals.existing) > 0.005 ? totals.existing : null,

          periodTotal:
            Math.abs(totals.aggregate) > 0.005 ? totals.aggregate : null,
        };
      });
  }, [
    schedule,
    datedDate,
    firstPrincipalDate,
    principalFrequency,
  ]);

  const hasExistingDebtService = useMemo(
    () =>
      periodTotals.some(
        (r) => Math.abs(num(r.existingDebtService, 0)) > 0.005
      ),
    [periodTotals]
  );

  const combined = useMemo(() => {
    if (!couponData.length) return [];
    const map = new Map();

    for (const b of couponData) {
      map.set(b.date, {
        date: b.date,
        principal: b.principal,
        interest: b.interest,
        total: b.total,
        existingDebtService: null,
        aggregateDebtService: null,
        periodTotal: null,
      });
    }

    for (const y of periodTotals) {
      if (map.has(y.date)) {
        map.set(y.date, {
          ...map.get(y.date),
          existingDebtService: y.existingDebtService,
          periodTotal: y.periodTotal,
        });
      } else {
        map.set(y.date, {
          date: y.date,
          principal: 0,
          interest: 0,
          total: 0,
          existingDebtService: y.existingDebtService,
          aggregateDebtService: y.periodTotal,
          periodTotal: y.periodTotal,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [couponData, periodTotals]);

  const maxVal = Math.max(
    ...combined.map((d) => d.total || 0),
    ...combined.map((d) => d.existingDebtService || 0),
    ...combined.map((d) => d.periodTotal ?? 0),
    0
  );

  const scale = useMemo(() => makeNiceScaleZeroBased(maxVal, 6, 1), [maxVal]);
  const yTickFormatter = (v) => `$${formatKMB(v)}`;
  const tooltipFormatter = (val, name) => [formatFull2dp(val, "$"), name];

  if (!schedule.length) {
    return (
      <SectionCard title="Debt Service">
        <div className="p-4 text-muted">No debt service schedule available.</div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Debt Service">
      {refundingComparison.length > 0 && (
        <div className="inline-flex items-center rounded-md border border-border overflow-hidden mb-4">
          {[
            ["refunding", "Refunding Bonds"],
            ["comparison", "Prior vs. Refunding"],
          ].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setView(key)} className={`px-3 py-1 text-sm font-medium ${view === key ? "btn btn-primary" : "bg-app text-muted"}`}>{label}</button>
          ))}
        </div>
      )}
      {view === "comparison" && refundingComparison.length > 0 ? (
        <div className="overflow-x-auto">
          <ExcelGrid columns={comparisonColumns} rows={refundingComparison} readOnly />
        </div>
      ) : (
        <>
      {/* ---------- KPIs ---------- */}
      {kpis ? (
        <div
          className={`mb-3 grid grid-cols-1 md:grid-cols-3 ${
            kpis.hasAdjustments ? "xl:grid-cols-5" : ""
          } gap-2 text-md`}
        >
          <div className="bg-card border border-border rounded-xl p-3">
            <div className="text-muted text-sm">Total Principal</div>
            <div className="font-medium">${fmt2(kpis.totalPrincipal)}</div>
          </div>

          <div className="bg-card border border-border rounded-xl p-3">
            <div className="text-muted text-sm">Total Interest</div>
            <div className="font-medium">${fmt2(kpis.totalInterest)}</div>
          </div>

          <div className="bg-card border border-border rounded-xl p-3">
            <div className="text-muted text-sm">Debt Service</div>
            <div className="font-medium">${fmt2(kpis.grossDebtService)}</div>
          </div>

          {kpis.hasAdjustments ? (
            <>
              <div className="bg-card border border-border rounded-xl p-3">
                <div className="text-muted text-sm">Total Adjustments</div>
                <div className="font-medium">${fmt2(kpis.totalAdjustments)}</div>
              </div>

              <div className="bg-card border border-border rounded-xl p-3">
                <div className="text-muted text-sm">Net Debt Service</div>
                <div className="font-medium">${fmt2(kpis.netDebtServiceTotal)}</div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {/* ---------- Chart ---------- */}
      <div
        ref={plotRef}
        className="h-96 bg-card border border-border rounded-xl p-4 mb-4 shadow-sm"
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={combined} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="date" allowDuplicatedCategory={false} />

            <YAxis
              domain={[scale.niceMin, scale.niceMax]}
              ticks={scale.ticks}
              tickFormatter={yTickFormatter}
            />

            <Tooltip content={<ChartTooltip formatter={tooltipFormatter} />} />

            <Legend
              verticalAlign="bottom"
              align="center"
              height={18}
              wrapperStyle={{
                bottom: 0,
                fontSize: 13,
                lineHeight: "1em",
                marginTop: 0,
                padding: 0,
              }}
            />

            <Bar
              dataKey="principal"
              stackId="a"
              fill="var(--chart-1)"
              name="Principal"
            />

            <Bar
              dataKey="interest"
              stackId="a"
              fill="var(--chart-2)"
              name="Interest"
            />

            {hasExistingDebtService ? (
              <Line
                type="monotone"
                dataKey="existingDebtService"
                stroke="var(--chart-4)"
                strokeWidth={2}
                dot={{ r: 2, fill: "var(--chart-4)" }}
                activeDot={{ r: 4 }}
                connectNulls
                name="Existing D/S"
              />
            ) : null}

            <Line
              type="monotone"
              dataKey="periodTotal"
              stroke="var(--chart-3)"
              strokeWidth={3}
              dot={{ r: 3, fill: "var(--chart-3)" }}
              activeDot={{ r: 5 }}
              connectNulls
              name="Period Total"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ---------- Table ---------- */}
      <DebtServiceTable rows={schedule} form={form} />
        </>
      )}
    </SectionCard>
  );
}
